import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { ArrowBack, Print } from '@mui/icons-material';
import StudentIdCard from '../components/StudentIdCard';
import { API_URL } from '../utils/api';

const ClassCardsPrint = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const grade = searchParams.get('grade');
  const classroom = searchParams.get('classroom') || '';

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        if (!grade || grade === 'all') {
          throw new Error('Pilih kelas dan rombel terlebih dahulu');
        }

        const qs = new URLSearchParams({ grade });
        if (classroom && classroom !== 'all') {
          qs.set('classroom', classroom);
        }

        const res = await fetch(`${API_URL}/api/employees/cards/batch?${qs}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.error || 'Gagal memuat kartu kelas');
        }
        setBatch(json.data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [grade, classroom, navigate]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !batch?.cards?.length) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error || 'Tidak ada kartu untuk dicetak'}</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/students')} sx={{ mt: 2 }}>
          Kembali ke Data Siswa
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f1f5f9', minHeight: '100vh' }}>
      <Box
        className="no-print"
        sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}
      >
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/students')}>
          Kembali
        </Button>
        <Button variant="contained" startIcon={<Print />} onClick={handlePrint}>
          Cetak Semua ({batch.count} kartu)
        </Button>
      </Box>

      <Typography className="no-print" align="center" sx={{ mb: 2, fontWeight: 700 }}>
        {batch.class_label} — {batch.count} siswa
      </Typography>
      <Typography className="no-print" align="center" variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
        Layout 4 kolom per halaman A4 · atur margin &quot;None&quot; / scale 100% saat cetak
      </Typography>

      <Box className="class-cards-print-grid">
        {batch.cards.map((card) => (
          <Box key={card.id} className="class-cards-print-item">
            <StudentIdCard card={card} compact />
          </Box>
        ))}
      </Box>

      <style>{`
        .class-cards-print-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
          justify-items: center;
          max-width: 900px;
          margin: 0 auto;
        }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; margin: 0; padding: 0; }
          .class-cards-print-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 3mm;
            max-width: none;
            width: 100%;
            padding: 2mm;
          }
          .class-cards-print-item {
            break-inside: avoid;
            page-break-inside: avoid;
            display: flex;
            justify-content: center;
          }
          .student-id-card-sheet {
            box-shadow: none !important;
            margin: 0 auto;
          }
        }
      `}</style>
    </Box>
  );
};

export default ClassCardsPrint;
